import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import AnalyticsIcons from '@/icons/AnalyticsIcons'
import { formatNumber } from '@/utils/function'
import {
    AlertTriangle,
    Ban,
    CheckCircle2,
    Clock,
    Flame,
    Snowflake,
    UserX,
} from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Loading } from 'vnetwork-ui'
import {
    getConversionRateStats,
    getPendingAlertsStats,
} from '../../../services'
import { encodeFilters, useLeadFilters } from '../LeadFiltersContext'

/** Lead category config: key = data field, label = i18n key */
const LEAD_CATEGORIES = [
    {
        key: 'warm_lead',
        label: 'warm-lead',
        icon: Flame,
        color: 'text-amber-500',
        bg: 'bg-amber-50',
        bar: 'bg-amber-400',
    },
    {
        key: 'sale_qualified_lead',
        label: 'sale-qualified-lead',
        icon: CheckCircle2,
        color: 'text-emerald-600',
        bg: 'bg-emerald-50',
        bar: 'bg-emerald-500',
    },
    {
        key: 'cool_lead',
        label: 'cool-lead',
        icon: Snowflake,
        color: 'text-blue-500',
        bg: 'bg-blue-50',
        bar: 'bg-blue-400',
    },
    {
        key: 'un_qualified_lead',
        label: 'unqualified',
        icon: Ban,
        color: 'text-red-500',
        bg: 'bg-red-50',
        bar: 'bg-red-400',
    },
] as const

/** Alert items config */
const ALERT_ITEMS = [
    {
        key: 'unassigned_over_1day' as const,
        label: 'unassigned-1d',
        icon: UserX,
        color: 'text-amber-500',
        bg: 'bg-amber-50',
    },
    {
        key: 'stale_unclassified_over_threshold' as const,
        label: 'total-assigned-unclassified',
        icon: Clock,
        color: 'text-orange-500',
        bg: 'bg-orange-50',
    },
] as const

const LeadsOverview: React.FC = () => {
    const { t } = useTranslation()
    const { filters } = useLeadFilters()

    const buildPayload = () => {
        const { q, ...encodableFilters } = filters
        return {
            q: q || undefined,
            filters: encodeFilters(encodableFilters) || undefined,
        }
    }

    const {
        data: statsData,
        isFetching: isFetchingStats,
        isLoading: isLoadingStats,
    } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.MARKETING,
            'leads-overview-stats',
            JSON.stringify(filters),
        ],
        queryFn: () => getConversionRateStats(buildPayload()),
        placeholderData: true,
    })

    const { data: alertsData } = useQueryWithHandler({
        queryKey: [
            QUERY_KEYS.MARKETING,
            'pending-alerts-stats',
            JSON.stringify(filters),
        ],
        queryFn: () => getPendingAlertsStats(buildPayload()),
        refetchInterval: 60000,
        placeholderData: true,
    })

    const stats = statsData?.result
    const alerts = alertsData?.result

    // Segmented bar widths
    const barSegments = useMemo(() => {
        if (!stats || !stats.total_leads) return []
        const total = stats.total_leads
        return LEAD_CATEGORIES.map((cat) => ({
            ...cat,
            value: stats[cat.key] ?? 0,
            pct: ((stats[cat.key] ?? 0) / total) * 100,
        }))
    }, [stats])

    // During a rolling deployment an old API pod may not yet return the
    // additive threshold fields. Keep its legacy count and label paired until
    // the new contract is available, then switch to the 24-hour metric.
    const hasStaleThreshold =
        alerts?.stale_unclassified_threshold_hours !== undefined
    const staleAlertCount = hasStaleThreshold
        ? (alerts?.stale_unclassified_over_threshold ?? 0)
        : (alerts?.stale_over_2days ?? 0)
    const staleAlertLabel = hasStaleThreshold
        ? `${t('total-assigned-unclassified')} > ${alerts?.stale_unclassified_threshold_hours}h`
        : t('stale-2d')
    const totalAlerts = (alerts?.unassigned_over_1day ?? 0) + staleAlertCount

    if (isLoadingStats) return <Loading.FadingDots />
    if (!stats) return null

    return (
        <div
            className={`bg-white rounded-sm p-4 h-full transition-opacity duration-200 ${isFetchingStats ? 'opacity-50' : ''}`}
            data-tour="leads-overview"
        >
            {/* Header row */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <AnalyticsIcons className="w-4 h-4 text-primary" />
                    <span className="text-sm font-semibold text-primary">
                        {t('leads-overview')}
                    </span>
                </div>
                <span className="text-lg font-bold text-gray-800 tabular-nums">
                    {formatNumber(stats.total_leads)}
                </span>
            </div>

            {/* Classification progress bar */}
            <div className="mb-1">
                <div className="flex items-center justify-between mb-1">
                    <span className="text-[11px] text-gray-500">
                        {t('classified')}:{' '}
                        {formatNumber(stats.total_classified)}/
                        {formatNumber(stats.total_leads)}
                    </span>
                    <span className="text-[11px] font-medium text-gray-600 tabular-nums">
                        {Math.round(stats.classification_rate)}%
                    </span>
                </div>
                <div className="h-2 rounded-full overflow-hidden bg-gray-100 flex">
                    {barSegments.map((seg) =>
                        seg.pct > 0 ? (
                            <div
                                key={seg.key}
                                className={`${seg.bar} h-full transition-all duration-300`}
                                style={{ width: `${seg.pct}%` }}
                            />
                        ) : null
                    )}
                </div>
            </div>

            {/* Category stats - 4 columns */}
            <div className="grid grid-cols-4 gap-2 mt-3">
                {LEAD_CATEGORIES.map((cat) => {
                    const Icon = cat.icon
                    const value = stats[cat.key] ?? 0
                    return (
                        <div
                            key={cat.key}
                            className={`${cat.bg} rounded-md px-2 py-2 text-center`}
                        >
                            <Icon
                                className={`w-3.5 h-3.5 ${cat.color} mx-auto mb-1`}
                            />
                            <div
                                className={`text-sm font-semibold ${cat.color} tabular-nums`}
                            >
                                {formatNumber(value)}
                            </div>
                            <div className="text-[10px] text-gray-500 leading-tight mt-0.5">
                                {t(cat.label)}
                            </div>
                        </div>
                    )
                })}
            </div>

            {/* Alerts - same card grid style */}
            {totalAlerts > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center gap-1.5 mb-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                        <span className="text-[11px] font-medium text-amber-600">
                            {t('pending-leads')}
                        </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        {ALERT_ITEMS.map((item) => {
                            const Icon = item.icon
                            const value =
                                item.key === 'stale_unclassified_over_threshold'
                                    ? staleAlertCount
                                    : (alerts?.[item.key] ?? 0)
                            return (
                                <div
                                    key={item.key}
                                    className={`${item.bg} rounded-md px-2 py-2 text-center`}
                                >
                                    <Icon
                                        className={`w-3.5 h-3.5 ${item.color} mx-auto mb-1`}
                                    />
                                    <div
                                        className={`text-sm font-semibold ${item.color} tabular-nums`}
                                    >
                                        {formatNumber(value)}
                                    </div>
                                    <div className="text-[10px] text-gray-500 leading-tight mt-0.5">
                                        {item.key ===
                                        'stale_unclassified_over_threshold'
                                            ? staleAlertLabel
                                            : t(item.label)}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

export default LeadsOverview
